import React from "react";
import { Link } from "react-router-dom";
import { api, resolveImg } from "../lib/api";
import useSEO from "../lib/useSEO";

function ReportCard({ r, featured = false }) {
  return (
    <Link
      to={`/market-analytics/${r.slug}`}
      data-testid={`market-card-${r.slug}`}
      className={`group block bg-white border border-zinc-200 hover:border-[#031833] transition-colors overflow-hidden ${
        featured ? "lg:col-span-2 lg:row-span-2" : ""
      }`}
    >
      <div className={`relative bg-zinc-100 overflow-hidden ${featured ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
        {r.cover_image_url ? (
          <img
            src={resolveImg(r.cover_image_url)}
            alt={r.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#031833] to-[#0A2455]">
            <span className="font-mono-tech text-[10px] uppercase text-[#FFCC00]">// REPORT</span>
          </div>
        )}
        {r.is_featured && (
          <span className="absolute top-3 left-3 bg-[#FFCC00] text-zinc-950 font-mono-tech text-[9px] uppercase px-2 py-1">
            ★ Featured
          </span>
        )}
        {r.gated && (
          <span className="absolute top-3 right-3 bg-[#031833] text-white font-mono-tech text-[9px] uppercase px-2 py-1">
            🔒 Gated
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono-tech text-[9px] uppercase text-[#031833] bg-zinc-100 px-2 py-0.5">{r.category}</span>
          <span className="font-mono-tech text-[9px] text-zinc-400">
            {new Date(r.published_at).toLocaleDateString("en-AE", { year: "numeric", month: "short", day: "numeric" })}
          </span>
        </div>
        <h3 className={`font-display tracking-tighter mb-2 leading-tight ${featured ? "text-3xl" : "text-xl"}`}>
          {r.title}
        </h3>
        <p className="text-sm text-zinc-600 leading-relaxed line-clamp-3 mb-3">{r.summary}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {(r.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="font-mono-tech text-[9px] text-zinc-500 border border-zinc-200 px-1.5 py-0.5">
              #{t}
            </span>
          ))}
        </div>
        <div className="mt-4 font-mono-tech text-[11px] uppercase text-[#031833] group-hover:text-[#FFCC00] transition-colors">
          Read report →
        </div>
      </div>
    </Link>
  );
}

export default function MarketAnalytics() {
  const [reports, setReports] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [cat, setCat] = React.useState("");

  useSEO({
    title: "Market Analytics — Dubai Industrial Real Estate Reports | WarehousesUAE",
    description:
      "Quarterly market reports, zone-level insights, and industrial real estate trend analysis for Dubai's warehouse, logistics and industrial property sectors.",
    canonical: "/market-analytics",
  });

  React.useEffect(() => {
    api.get("/market-reports").then((r) => {
      setReports(r.data);
      setLoading(false);
    });
  }, []);

  const categories = Array.from(new Set(reports.map((r) => r.category))).sort();
  const filtered = cat ? reports.filter((r) => r.category === cat) : reports;
  const featured = filtered.find((r) => r.is_featured);
  const rest = filtered.filter((r) => r !== featured);

  return (
    <div className="min-h-screen bg-zinc-50">
      <section className="bg-[#031833] text-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-20">
          <div className="font-mono-tech text-[10px] uppercase text-[#FFCC00] mb-3">// INSIGHTS</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-4 max-w-3xl">
            MARKET<br />ANALYTICS.
          </h1>
          <p className="font-mono-tech text-sm text-zinc-300 max-w-2xl leading-relaxed">
            Quarterly market reports, zone-level pricing trends, and industrial real estate intelligence
            for Dubai and the wider UAE. Built for occupiers, developers, and investors.
          </p>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        {categories.length > 0 && (
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            <button
              data-testid="market-filter-all"
              onClick={() => setCat("")}
              className={`font-mono-tech text-[11px] uppercase px-3 py-2 whitespace-nowrap transition-colors ${
                !cat ? "bg-[#031833] text-[#FFCC00]" : "bg-white border border-zinc-200 text-zinc-700 hover:border-zinc-900"
              }`}
            >
              All ({reports.length})
            </button>
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`market-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`font-mono-tech text-[11px] uppercase px-3 py-2 whitespace-nowrap transition-colors ${
                  cat === c ? "bg-[#031833] text-[#FFCC00]" : "bg-white border border-zinc-200 text-zinc-700 hover:border-zinc-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 font-mono-tech text-xs text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-16 text-center bg-white">
            <div className="font-display text-3xl tracking-tighter mb-2">No reports yet</div>
            <p className="text-zinc-500 text-sm">Check back soon — our research team publishes quarterly insights.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
            {featured && <ReportCard r={featured} featured />}
            {rest.map((r) => <ReportCard key={r.id} r={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}
